import React, { useState } from "react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "./ui/popover";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ArrowUpRight, ArrowDownRight, Sparkles } from "lucide-react";

const PRESETS = [5, 10, 15, 20, 25, 30, 40, 50];

/**
 * Compact ±% budget picker. Click trigger → popover with chips for both directions,
 * custom % input, live preview, and Apply.
 *
 * Props:
 *  - currentBudget: number (current daily budget)
 *  - busy: boolean (disable Apply while a request is in flight)
 *  - testid: string (for the trigger button)
 *  - defaultPct: number (default 20)
 *  - onApply({ direction: 'increase'|'decrease', pct: number }) => Promise|void
 *  - disabled: boolean
 *  - currency: string (default '£')
 */
export default function BudgetQuickPicker({
  currentBudget,
  busy,
  testid,
  defaultPct = 20,
  defaultDirection = "increase",
  onApply,
  disabled = false,
  currency = "£",
}) {
  const [open, setOpen] = useState(false);
  const [direction, setDirection] = useState(defaultDirection);
  const [pct, setPct] = useState(defaultPct);
  const [customPct, setCustomPct] = useState("");

  // Reset to the default direction every time the popover opens so red-CPA campaigns
  // pre-select 'decrease' (or whatever the caller passes).
  React.useEffect(() => {
    if (open) setDirection(defaultDirection);
  }, [open, defaultDirection]);

  const factor = 1 + ((direction === "increase" ? 1 : -1) * Number(pct || 0)) / 100;
  const preview = currentBudget != null && currentBudget > 0
    ? Math.max(0, Number(currentBudget) * factor)
    : null;

  const handleApply = async () => {
    if (!pct || pct <= 0) return;
    await onApply?.({ direction, pct: Number(pct) });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          data-testid={testid}
          variant="outline"
          size="sm"
          disabled={disabled}
          className="rounded-none h-6 px-2 border-zinc-300 hover:border-zinc-900 hover:bg-zinc-50 font-mono text-[10px] uppercase tracking-wider gap-1"
          title="Adjust daily budget"
        >
          <Sparkles className="w-3 h-3" /> ±%
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 rounded-none p-0 border-zinc-300"
        data-testid={`${testid}-popover`}
      >
        <div className="px-3 py-2 border-b border-zinc-200 flex items-center justify-between">
          <div className="data-label">Adjust budget</div>
          <div className="text-[10px] font-mono text-zinc-500">
            current {currency}{Number(currentBudget || 0).toFixed(2)}
          </div>
        </div>

        {/* Direction toggle */}
        <div className="grid grid-cols-2 border-b border-zinc-200">
          <button
            onClick={() => setDirection("increase")}
            data-testid={`${testid}-dir-increase`}
            className={`flex items-center justify-center gap-1 py-2 text-xs font-mono uppercase tracking-wider transition-colors ${
              direction === "increase" ? "bg-emerald-50 text-emerald-700" : "text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            <ArrowUpRight className="w-3 h-3" /> Increase
          </button>
          <button
            onClick={() => setDirection("decrease")}
            data-testid={`${testid}-dir-decrease`}
            className={`flex items-center justify-center gap-1 py-2 text-xs font-mono uppercase tracking-wider border-l border-zinc-200 transition-colors ${
              direction === "decrease" ? "bg-amber-50 text-amber-700" : "text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            <ArrowDownRight className="w-3 h-3" /> Decrease
          </button>
        </div>

        {/* Preset chips */}
        <div className="px-3 py-3">
          <div className="data-label mb-2">By how much?</div>
          <div className="grid grid-cols-4 gap-1">
            {PRESETS.map((v) => (
              <button
                key={v}
                onClick={() => { setPct(v); setCustomPct(""); }}
                data-testid={`${testid}-preset-${v}`}
                className={`h-8 text-xs font-mono border transition-colors ${
                  pct === v && !customPct
                    ? "bg-zinc-900 text-white border-zinc-900"
                    : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"
                }`}
              >
                {v}%
              </button>
            ))}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <Input
              type="number"
              min="1"
              max="500"
              step="1"
              placeholder="custom"
              value={customPct}
              onChange={(e) => {
                const v = e.target.value;
                setCustomPct(v);
                if (v) setPct(Number(v));
              }}
              data-testid={`${testid}-custom`}
              className="rounded-none border-zinc-300 h-8 text-xs font-mono"
            />
            <span className="text-xs text-zinc-500 font-mono">%</span>
          </div>
        </div>

        {/* Preview + Apply */}
        <div className="px-3 py-2 border-t border-zinc-200 bg-zinc-50">
          <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Preview</div>
          {preview != null ? (
            <div className="font-mono text-sm flex items-center gap-2">
              <span className="text-zinc-500">{currency}{Number(currentBudget).toFixed(2)}</span>
              <span className="text-zinc-400">→</span>
              <span className={direction === "increase" ? "text-emerald-700 font-semibold" : "text-amber-700 font-semibold"}>
                {currency}{preview.toFixed(2)}
              </span>
              <span className="text-[10px] text-zinc-500">
                ({direction === "increase" ? "+" : "−"}{pct}%)
              </span>
            </div>
          ) : (
            <div className="text-xs text-zinc-400 font-mono">Budget unknown</div>
          )}
          <div className="mt-2 flex gap-2">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              className="rounded-none flex-1 h-8 text-xs font-mono uppercase tracking-wider"
            >
              Cancel
            </Button>
            <Button
              onClick={handleApply}
              disabled={busy || !pct || pct <= 0}
              data-testid={`${testid}-apply`}
              className={`rounded-none flex-1 h-8 text-xs font-mono uppercase tracking-wider text-white ${
                direction === "increase" ? "bg-emerald-700 hover:bg-emerald-800" : "bg-amber-700 hover:bg-amber-800"
              }`}
            >
              {busy ? "…" : "Apply"}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
